/**
 * French texts of the entry journey (E1 `intro`, E2 `telegram`, E3 `channels` and E4 `tradingAccount`; E5 adds its own section),
 * from specs/S3-intro/spec.md §4, specs/S4-telegram-login/spec.md §4 and specs/S5-channel-selection/spec.md §4 (EN from `Canaux.html` and `CanauxVide.html`). Provisional until Q3 (tone,
 * "tu") and Q15 (EN mockups). The phone step of `telegram` has no mockup (Q70).
 *
 * `telegram.code.intro` takes the digit count and the masked number the API sent, as text.
 *
 * `intro.invitedBy` holds a `<strong>` tag and the `{{name}}` of the affiliate: it is rendered with
 * `Trans`, and the name is interpolated as text (S3 R11, AC14). `intro.step2` says "in money"
 * until Q27 decides between euros and the account currency.
 */
export const onboardingFr = {
  intro: {
    title: 'Copie automatiquement les signaux de tes canaux Telegram.',
    step1: 'Tu choisis les canaux que tu suis déjà sur Telegram.',
    step2: 'Tu règles ton risque pour chaque canal, en argent, pas en formules.',
    step3: 'FXMate passe les trades sur ton compte MT5, et tu peux tout arrêter d’un geste.',
    readOnlyNote:
      'FXMate lit uniquement les canaux que tu choisis. Il ne lit jamais tes conversations privées et n’envoie jamais de message à ta place.',
    invitedBy: 'Invité par <strong>{{name}}</strong>',
    start: 'Commencer · environ 2 min',
  },
  telegram: {
    step: 'Étape 1 sur 4 · Identification',
    sending: 'Envoi…',
    phone: {
      title: 'Quel est ton numéro Telegram ?',
      help: 'Telegram t’envoie un code dans ton app. Nous n’utilisons ce numéro que pour demander ce code.',
      share: 'Partager mon numéro',
      typeInstead: 'Saisir mon numéro',
      label: 'Ton numéro Telegram',
      hint: 'Avec l’indicatif, par exemple +33…',
      submit: 'Recevoir le code',
    },
    code: {
      title: 'Saisis le code reçu dans Telegram',
      intro:
        'Telegram vient d’envoyer un code à {{length}} chiffres dans ton app, pour le compte {{phone}}.',
      label: 'Code de connexion',
      twoFactorTitle: 'Tu as aussi un mot de passe Telegram ?',
      twoFactorBody:
        'Si tu as activé la validation en deux étapes, nous te le demanderons juste après. Telegram l’exige pour ouvrir une session.',
      confirm: 'Valider',
      newCode: 'Recevoir un nouveau code',
    },
    password: {
      accepted: 'Code accepté',
      title: 'Ton mot de passe Telegram',
      why: 'Tu as activé la validation en deux étapes. Telegram demande ce mot de passe pour ouvrir la session qui lira tes canaux.',
      label: 'Mot de passe Telegram',
      help: 'Celui que tu as choisi dans Telegram › Confidentialité et sécurité › Validation en deux étapes. Ce n’est pas le code à 5 chiffres.',
      privacyTitle: 'Personne ne peut le lire',
      privacy:
        'Il sert uniquement à ouvrir la session Telegram de FXMate. Personne dans l’équipe 4Affiliates n’y a accès.',
      confirm: 'Valider',
    },
  },
  channels: {
    step: 'Étape 2 sur 4 · Canaux',
    title: 'Quels canaux veux-tu copier ?',
    loading: 'Chargement de tes canaux…',
    searchLabel: 'Rechercher un canal',
    searchPlaceholder_one: 'Rechercher dans ton canal',
    searchPlaceholder_other: 'Rechercher parmi tes {{count}} canaux',
    recommendedTitle: 'Recommandés par ton affilié',
    affiliateChannel: 'Le canal de ton affilié',
    allTitle: 'Tous tes canaux',
    copy: 'Copier {{name}}',
    inactive: 'Inactif',
    removalNote:
      'Retirer un canal arrête la copie de ses prochains signaux. Les positions déjà ouvertes par ce canal restent suivies.',
    removed:
      '{{name}} ne sera plus copié. Les positions déjà ouvertes par ce canal restent suivies.',
    noResult: 'Aucun canal ne correspond à « {{query}} ».',
    selectedNone: 'Aucun canal sélectionné',
    selected_one: '{{count}} canal sélectionné',
    selected_other: '{{count}} canaux sélectionnés',
    continue: 'Continuer',
    retry: 'Réessayer',
    signInAgain: 'M’identifier à nouveau',
    empty: {
      title: 'Tu n’es membre d’aucun canal de signaux',
      body: 'FXMate copie uniquement les canaux que tu as déjà rejoints dans Telegram. Rejoins d’abord un canal, puis reviens ici.',
      join: 'Rejoindre le canal dans Telegram',
      refresh: 'J’ai rejoint un canal, actualiser',
    },
  },
  tradingAccount: {
    step: 'Étape 3 sur 4 · Compte de trading',
    title: 'Connecte ton compte MT5',
    platformLabel: 'Plateforme',
    platformValue: 'MetaTrader 5',
    accountNumber: {
      label: 'Numéro de compte',
      help: 'Dans MetaTrader : menu Compte, c’est le nombre affiché sous ton nom.',
    },
    password: {
      label: 'Mot de passe principal',
      help: 'Celui reçu par email de ton broker à l’ouverture du compte. Pas le mot de passe « investisseur » : il ne permet pas de passer d’ordres.',
    },
    server: {
      label: 'Serveur',
      help: 'Dans MetaTrader : écran de connexion, champ Serveur.',
    },
    check: 'Vérifier le compte',
    checkAgain: 'Vérifier à nouveau',
    reconnect: 'Reconnecter',
    verifying: 'Vérification de ton compte…',
    loading: 'Chargement…',
    retry: 'Réessayer',
    found: {
      title: 'Compte trouvé. C’est bien le tien ?',
      summary: '{{platform}} · ••••{{last4}} · {{server}}',
      holder: 'Titulaire',
      currency: 'Devise',
      balance: 'Solde',
      balanceUnavailable: 'Solde indisponible',
      demo: 'Compte démo',
    },
    can: 'FXMate pourra ouvrir, modifier et fermer des positions sur ce compte, selon les canaux et réglages que tu choisis.',
    never:
      'FXMate ne pourra jamais faire de dépôt, de retrait, de virement ni modifier ton profil chez le broker.',
    consent: {
      label: 'J’autorise FXMate à passer des ordres sur mon compte ••••{{last4}}.',
      submit: 'Autoriser et continuer',
    },
    readOnly: {
      title: 'Ce compte est en lecture seule',
      whereTitle: 'Où le trouver ?',
      whereBody:
        'Dans l’email reçu de ton broker à l’ouverture du compte. Tu peux aussi le changer depuis ton espace client chez le broker.',
    },
    unreachable: { hint: 'Ton compte se termine par {{last4}}.' },
    connected: { title: 'Ton compte de trading est connecté.', continue: 'Continuer' },
  },
  firstSettings: {
    step: 'Étape 4 sur 4 · Réglages',
    title: 'Règle ton premier canal',
    nothingCopied: 'Rien n’est copié tant que tu n’as pas confirmé.',
    others_one: 'Tu régleras ton autre canal depuis l’accueil.',
    others_other: 'Tu régleras tes {{count}} autres canaux depuis l’accueil.',
    loading: 'Chargement…',
    later: 'Régler plus tard',
    continueHome: 'Continuer vers l’accueil',
  },
};

/**
 * Shape of the entry journey texts. Derived from the French object, like `HomeMessages`: i18next
 * derives the translation keys from it, and an `interface` would not work there.
 */
export type OnboardingMessages = typeof onboardingFr;

/** English texts of the entry journey. Provisional until Q3 and Q15. */
export const onboardingEn: OnboardingMessages = {
  intro: {
    title: 'Copy the signals from your Telegram channels, automatically.',
    step1: 'You choose the channels you already follow on Telegram.',
    step2: 'You set your risk for each channel, in money, not formulas.',
    step3: 'FXMate places the trades on your MT5 account, and you can stop everything in one tap.',
    readOnlyNote:
      'FXMate only reads the channels you choose. It never reads your private chats and never sends any message on your behalf.',
    invitedBy: 'Invited by <strong>{{name}}</strong>',
    start: 'Get started · about 2 min',
  },
  telegram: {
    step: 'Step 1 of 4 · Sign-in',
    sending: 'Sending…',
    phone: {
      title: 'What is your Telegram phone number?',
      help: 'Telegram sends you a code in your app. We only use this number to ask for that code.',
      share: 'Share my number',
      typeInstead: 'Type it instead',
      label: 'Your Telegram phone number',
      hint: 'With the country code, for example +33…',
      submit: 'Get the code',
    },
    code: {
      title: 'Enter the code received in Telegram',
      intro: 'Telegram just sent a {{length}}-digit code to your app, for the account {{phone}}.',
      label: 'Login code',
      twoFactorTitle: 'Do you also have a Telegram password?',
      twoFactorBody:
        'If you turned on two-step verification, we’ll ask for it right after. Telegram requires it to open a session.',
      confirm: 'Confirm',
      newCode: 'Get a new code',
    },
    password: {
      accepted: 'Code accepted',
      title: 'Your Telegram password',
      why: 'You turned on two-step verification. Telegram asks for this password to open the session that will read your channels.',
      label: 'Telegram password',
      help: 'The one you chose in Telegram › Privacy and Security › Two-Step Verification. It is not the 5-digit code.',
      privacyTitle: 'No one can read it',
      privacy:
        'It is only used to open FXMate’s Telegram session. No one on the 4Affiliates team has access to it.',
      confirm: 'Confirm',
    },
  },
  channels: {
    step: 'Step 2 of 4 · Channels',
    title: 'Which channels do you want to copy?',
    loading: 'Loading your channels…',
    searchLabel: 'Search for a channel',
    searchPlaceholder_one: 'Search your channel',
    searchPlaceholder_other: 'Search your {{count}} channels',
    recommendedTitle: 'Recommended by your affiliate',
    affiliateChannel: 'Your affiliate’s channel',
    allTitle: 'All your channels',
    copy: 'Copy {{name}}',
    inactive: 'Inactive',
    removalNote:
      'Removing a channel stops copying its next signals. Positions already opened by that channel keep being tracked.',
    removed:
      '{{name}} will no longer be copied. Positions already opened by that channel keep being tracked.',
    noResult: 'No channel matches “{{query}}”.',
    selectedNone: 'No channel selected',
    selected_one: '{{count}} channel selected',
    selected_other: '{{count}} channels selected',
    continue: 'Continue',
    retry: 'Try again',
    signInAgain: 'Sign in again',
    empty: {
      title: 'You are not a member of any signal channel',
      body: 'FXMate only copies channels you have already joined in Telegram. Join a channel first, then come back here.',
      join: 'Join the channel in Telegram',
      refresh: 'I joined a channel, refresh',
    },
  },
  tradingAccount: {
    step: 'Step 3 of 4 · Trading account',
    title: 'Connect your MT5 account',
    platformLabel: 'Platform',
    platformValue: 'MetaTrader 5',
    accountNumber: {
      label: 'Account number',
      help: 'In MetaTrader: Account menu, it’s the number shown under your name.',
    },
    password: {
      label: 'Master password',
      help: 'The one your broker emailed you when the account was opened. Not the “investor” password: it can’t place orders.',
    },
    server: {
      label: 'Server',
      help: 'In MetaTrader: login screen, Server field.',
    },
    check: 'Check the account',
    checkAgain: 'Check again',
    reconnect: 'Reconnect',
    verifying: 'Checking your account…',
    loading: 'Loading…',
    retry: 'Try again',
    found: {
      title: 'Account found. Is it yours?',
      summary: '{{platform}} · ••••{{last4}} · {{server}}',
      holder: 'Holder',
      currency: 'Currency',
      balance: 'Balance',
      balanceUnavailable: 'Balance unavailable',
      demo: 'Demo account',
    },
    can: 'FXMate can open, modify and close positions on this account, based on the channels and settings you choose.',
    never:
      'FXMate can never make a deposit, withdrawal or transfer, or change your profile at the broker.',
    consent: {
      label: 'I authorize FXMate to place orders on my account ••••{{last4}}.',
      submit: 'Authorize and continue',
    },
    readOnly: {
      title: 'This account is read-only',
      whereTitle: 'Where to find it?',
      whereBody:
        'In the email your broker sent when the account was opened. You can also change it from your client area at the broker.',
    },
    unreachable: { hint: 'Your account ends in {{last4}}.' },
    connected: { title: 'Your trading account is connected.', continue: 'Continue' },
  },
  firstSettings: {
    step: 'Step 4 of 4 · Settings',
    title: 'Set up your first channel',
    nothingCopied: 'Nothing is copied until you confirm.',
    others_one: 'You’ll set up your other channel from Home.',
    others_other: 'You’ll set up your {{count}} other channels from Home.',
    loading: 'Loading…',
    later: 'Set up later',
    continueHome: 'Continue to Home',
  },
};
